import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, articles, categories } from '@/mocks/manga';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  const numId = parseInt(id || '0', 10);
  const comic = comics.find(c => c.id === numId);
  const article = articles.find(a => a.id === numId);
  const content = comic || article;
  const isComic = !!comic;

  if (!content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">内容不存在</h2>
            <p className="text-foreground-500 mb-6">该内容可能已被删除或链接失效</p>
            <Link to="/" className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </div>
        <Footer />
        <ScrollToTop />
      </div>
    );
  }

  const relatedComics = comics.filter(c => c.id !== numId).slice(0, 6);
  const catInfo = categories.find(c => c.id === ('category' in content ? content.category : ''));

  return (
    <>
      <SchemaOrg type={isComic ? 'Article' : 'Article'} data={{
        headline: content.title,
        description: 'summary' in content ? content.summary : content.description?.slice(0, 160),
        author: { '@type': 'Person', name: 'author' in content ? content.author : '' },
        datePublished: ('date' in content ? content.date : content.updatedAt),
        image: content.cover,
        publisher: { '@type': 'Organization', name: '51漫画网', url: 'https://www.51manhua.com' },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-10">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              ...(isComic && catInfo ? [{ label: catInfo.name, href: `/category/${catInfo.slug}` }] : []),
              ...(!isComic && article ? [{ label: '漫画资讯' }] : []),
              { label: content.title },
            ]} />

            <article className="mt-6">
              {/* Cover */}
              <div className="aspect-[21/9] rounded-lg overflow-hidden bg-background-200 mb-8">
                <img src={content.cover} alt={content.title} className="w-full h-full object-cover" />
              </div>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {isComic && comic && (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                )}
                {catInfo && isComic && (
                  <Link
                    to={`/category/${catInfo.slug}`}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {catInfo.name}
                  </Link>
                )}
                {article && (
                  <span className="text-sm text-foreground-500">{article.date}</span>
                )}
                {'author' in content && (
                  <span className="text-sm text-foreground-500">作者：{content.author}</span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 font-bold mb-2">
                {content.title}
              </h1>

              {/* Stats */}
              <div className="flex items-center gap-5 text-sm text-foreground-500 mb-6">
                {'rating' in content && (
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-400"></i>
                    <strong className="text-foreground-900">{content.rating}</strong> 分
                  </span>
                )}
                {'views' in content && (
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {(content.views as number).toLocaleString()} 次阅读
                  </span>
                )}
                {'status' in content && (
                  <span>状态：<strong className="text-foreground-900">{content.status}</strong></span>
                )}
              </div>

              {/* Comic Detail */}
              {isComic && comic && (
                <>
                  <div className="bg-background-100 rounded-lg p-5 mb-8">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  </div>

                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-heading text-foreground-950 font-bold">章节列表</h3>
                      <span className="text-xs text-foreground-500">共 {comic.chapters.length} 话</span>
                    </div>
                    <div className="bg-background-100 rounded-lg divide-y divide-background-200">
                      {comic.chapters.map(ch => (
                        <div key={ch.num} className="flex items-center justify-between px-4 py-3 hover:bg-background-200 transition-colors cursor-pointer">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-foreground-900">
                              第{ch.num}话
                            </span>
                            <span className="text-sm text-foreground-600">{ch.title}</span>
                          </div>
                          <span className="text-xs text-foreground-500 flex-shrink-0 whitespace-nowrap">{ch.date}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Article Content */}
              {article && (
                <div
                  className="prose max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3 mb-12"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              )}

              {/* Related */}
              <div className="border-t border-background-200 pt-8 mt-8">
                <h3 className="text-lg font-heading text-foreground-950 font-bold mb-5">推荐阅读</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4" data-product-shop="">
                  {relatedComics.map(c => (
                    <ComicCard key={c.id} {...c} />
                  ))}
                </div>
              </div>
            </article>
          </div>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}